import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MapPin, Play, Pause, Volume2, VolumeX } from "lucide-react";
import { HERO } from "../constants/testIds";

// Sobha-style: background video with poster fallback. Muted + autoplay + loop.
const HERO_VIDEO_1 = "https://videos.pexels.com/video-files/7578540/7578540-hd_1280_720_30fps.mp4";
const HERO_VIDEO_2 = "https://videos.pexels.com/video-files/5586205/5586205-hd_1920_1080_30fps.mp4";
const HERO_POSTER =
  "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=2000&q=85";

const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { delay: 2.9 + i * 0.12, duration: 1.0, ease: [0.16, 1, 0.3, 1] },
  }),
};

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: 3.9 + i * 0.08, duration: 0.8 } }),
};

export default function Hero() {
  const ref = useRef(null);
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(true);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.02, 1.12]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-15%"]);

  // Ensure autoplay kicks off even if a source fails to load quickly
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const tryPlay = () => v.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    tryPlay();
    v.addEventListener("canplay", tryPlay, { once: true });
    v.addEventListener("loadeddata", tryPlay, { once: true });
    return () => {
      v.removeEventListener("canplay", tryPlay);
      v.removeEventListener("loadeddata", tryPlay);
    };
  }, []);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); } else { v.pause(); setPlaying(false); }
  };
  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted; setMuted(v.muted);
  };

  return (
    <section
      ref={ref}
      data-testid={HERO.section}
      className="relative min-h-[100svh] bg-obsidian text-editorial overflow-hidden"
    >
      {/* Parallax background video with vignette */}
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <video
          ref={videoRef}
          className="w-full h-full object-cover"
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          poster={HERO_POSTER}
        >
          <source src={HERO_VIDEO_1} type="video/mp4" />
          <source src={HERO_VIDEO_2} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-obsidian/55" />
        <div className="absolute inset-0 bg-gradient-to-b from-obsidian/40 via-transparent to-obsidian" />
        <div className="absolute inset-0 bg-gradient-to-r from-obsidian/40 via-transparent to-transparent" />
      </motion.div>

      {/* Top corner meta */}
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.8, duration: 0.8 }}
        className="absolute top-[96px] left-6 md:left-10 z-10 flex items-center gap-3 chapter-num text-editorial/70"
      >
        <span className="w-8 h-px bg-editorial/40" />
        <span>001 / KLIVING · MMXXV</span>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.8, duration: 0.8 }}
        className="absolute top-[96px] right-6 md:right-10 z-10 flex items-center gap-2 chapter-num text-editorial/70"
      >
        <MapPin className="w-3 h-3" /> 20.29°N · 85.82°E — BHUBANESWAR
      </motion.div>

      {/* Main headline */}
      <motion.div
        style={{ y: textY }}
        className="relative z-10 min-h-[100svh] flex flex-col justify-center px-6 md:px-10 max-w-[1400px] mx-auto"
      >
        <div className="max-w-[1100px]">
          <motion.div
            initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 2.7, duration: 0.8 }}
            className="chapter-num text-lime_kl mb-6 md:mb-10"
          >
            — LANDMARK DEVELOPMENTS · SINCE 2019
          </motion.div>

          <h1 className="font-serif-display text-editorial leading-[0.92] tracking-[-0.02em] text-[42px] sm:text-[72px] md:text-[46px] lg:text-[128px]  ">
            <span className="mask-line"><motion.span custom={0} variants={lineVariants} initial="hidden" animate="show" className="italic font-light">Crafting</motion.span></span>
            <span className="mask-line"><motion.span custom={1} variants={lineVariants} initial="hidden" animate="show">Landmark</motion.span></span>
            <span className="mask-line"><motion.span custom={2} variants={lineVariants} initial="hidden" animate="show">
              Developments<span className="text-lime_kl">.</span>
            </motion.span></span>
          </h1>

          <div className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
            <motion.p
              variants={fadeUp} initial="hidden" animate="show" custom={0}
              className="md:col-span-7 text-editorial/80 text-base md:text-lg leading-relaxed max-w-lg"
            >
              A boutique developer building residential and commercial landmarks in Bhubaneswar — where materials are chosen slowly, drawings are drawn twice, and every plot is treated as public architecture.
            </motion.p>

            <motion.div variants={fadeUp} initial="hidden" animate="show" custom={1} 
            className="md:col-span-6 flex flex-row items-start md:items-end gap-3">
              <a href="#projects" data-testid={HERO.primaryCta} className="btn-kl w-full sm:w-auto justify-center md:justify-start bg-editorial text-obsidian border-editorial hover:bg-lime_kl hover:border-lime_kl">
                View Portfolio <span className="btn-dot" />
              </a>
              <a href="#contact" data-testid={HERO.secondaryCta} className="btn-kl w-full sm:w-auto justify-center md:justify-start text-editorial border-editorial/40 hover:border-editorial">
                Schedule a Visit <span className="btn-dot" />
              </a>
            </motion.div>
          </div>
        </div>

        {/* Bottom-left — SCROLL + Video controls */}
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 4.4, duration: 0.8 }}
          className="absolute bottom-8 left-6 md:left-10 flex items-center gap-5 text-editorial/80"
        >
          <div className="hidden md:flex items-center gap-2">
            <button
              onClick={togglePlay}
              className="w-10 h-10 grid place-items-center border border-editorial/40 text-editorial hover:bg-editorial hover:text-obsidian transition-colors duration-300"
              aria-label={playing ? "Pause video" : "Play video"}
            >
              {playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
            </button>
            <button
              onClick={toggleMute}
              className="w-10 h-10 grid place-items-center border border-editorial/40 text-editorial hover:bg-editorial hover:text-obsidian transition-colors duration-300"
              aria-label={muted ? "Unmute" : "Mute"}
            >
              {muted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
            </button>
          </div>
          <div className="flex items-center gap-2">
            <ArrowDown className="w-4 h-4 animate-bounce" />
            <span className="chapter-num">SCROLL</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 4.4, duration: 0.8 }}
          className="absolute bottom-8 right-6 md:right-10 grid grid-cols-3 gap-6 text-editorial/70 text-right"
        >
          <div>
            <div className="font-serif-display text-3xl md:text-4xl text-editorial">14</div>
            <div className="chapter-num mt-1">PROJECTS</div>
          </div>
          <div>
            <div className="font-serif-display text-3xl md:text-4xl text-editorial">2.8M</div>
            <div className="chapter-num mt-1">SQ. FT.</div>
          </div>
          <div>
            <div className="font-serif-display text-3xl md:text-4xl text-editorial">6</div>
            <div className="chapter-num mt-1">YEARS</div>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
}

